import { Injectable } from '@angular/core';
import { Observable, BehaviorSubject } from 'rxjs';
import { map } from 'rxjs/operators';
import { RoutingService } from 'src/app/service/routing.service';
import { ConfigService } from 'src/app/service/config.service';
import { ReportingApiService } from 'src/app/shared/services/reporting-api/reporting-api.service';

/**
 * Cockpit `score` values that have a corresponding backend `report_type`.
 * `bb` is included because it's aliased to `investment` on the wire (see
 * SCORE_TO_REPORT_TYPE below). Other cockpit score types — `sentiment`,
 * `credit_risk`, custom UUIDs — have no report variant on the backend and
 * are filtered out of the report-form dropdown.
 */
export const REPORT_SUPPORTED_SCORES: ReadonlySet<string> = new Set([
  'credit_watch',
  'esg',
  'esg_risk',
  'immediate_risk',
  'general_risk',
  'eb_sim',
  'eb_sim_risk',
  'bb',
]);

const SCORE_TO_REPORT_TYPE: Record<string, string> = {
  bb: 'investment',
};

const REPORT_TYPE_TO_SCORE: Record<string, string> = {
  investment: 'bb',
};

export function scoreToReportType(score: string): string {
  return SCORE_TO_REPORT_TYPE[score] ?? score;
}

export function reportTypeToScore(reportType: string): string {
  return REPORT_TYPE_TO_SCORE[reportType] ?? reportType;
}

export interface CompanyPayload {
  kind: 'company';
  companyId: string;
}

export interface PortfolioPayload {
  kind: 'portfolio';
  portfolioId: string;
  portfolioName: string;
  companyIds: string[];
}

export interface QueryPayload {
  kind: 'query';
  queryName: string;
}

export type SubscriptionPayload =
  | CompanyPayload
  | PortfolioPayload
  | QueryPayload;

export interface ReportSubscription {
  id: string;
  email: string;
  schedule: string;
  reportType: string;
  reportFormat: string;
  recipients: string[];
  payload: SubscriptionPayload;
  active: boolean;
  createdOn?: string;
  updatedOn?: string;
}

export interface CreateSubscriptionDto {
  schedule: string;
  reportType: string;
  reportFormat: string;
  recipients: string[];
  payload: SubscriptionPayload;
}

export interface UpdateSubscriptionDto {
  schedule?: string;
  reportType?: string;
  reportFormat?: string;
  recipients?: string[];
  payload?: SubscriptionPayload;
  active?: boolean;
}

interface ReportSubscriptionResponse {
  hits: ReportSubscription[];
}

const ENDPOINTS = {
  subscriptions: '/v1/subscriptions',
} as const;

@Injectable({
  providedIn: 'root',
})
export class ReportsService {
  private subscriptionsSubject = new BehaviorSubject<ReportSubscription[]>([]);
  public subscriptions$ = this.subscriptionsSubject.asObservable();

  constructor(
    private api: ReportingApiService,
    public routing: RoutingService,
    public config: ConfigService,
  ) {}

  getAllSubscriptions(): Observable<ReportSubscription[]> {
    return this.api
      .get<ReportSubscriptionResponse>(ENDPOINTS.subscriptions)
      .pipe(
        map((response) => {
          this.subscriptionsSubject.next(response.hits);
          return response.hits;
        }),
      );
  }

  createSubscription(
    dto: CreateSubscriptionDto,
  ): Observable<ReportSubscription> {
    return this.api.post<ReportSubscription>(ENDPOINTS.subscriptions, dto);
  }

  updateSubscription(
    id: string,
    dto: UpdateSubscriptionDto,
  ): Observable<ReportSubscription> {
    return this.api.put<ReportSubscription>(
      `${ENDPOINTS.subscriptions}/${id}`,
      dto,
    );
  }

  /**
   * Create when `id` is absent, update when present. Refreshes the list on success.
   */
  createOrUpdateSubscription(
    payload: CreateSubscriptionDto & { id?: string },
  ): Observable<ReportSubscription> {
    const { id, ...body } = payload;
    const obs$ = id
      ? this.updateSubscription(id, body)
      : this.createSubscription(body);
    return obs$.pipe(
      map((result) => {
        this.getAllSubscriptions().subscribe();
        return result;
      }),
    );
  }

  deleteSubscription(id: string): Observable<void> {
    return this.api.delete<void>(`${ENDPOINTS.subscriptions}/${id}`).pipe(
      map(() => {
        const current = this.subscriptionsSubject.getValue();
        this.subscriptionsSubject.next(current.filter((sub) => sub.id !== id));
      }),
    );
  }
}
